import React, { useState, useEffect, useContext, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import CircularProgress from "@material-ui/core/CircularProgress";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";
import Chip from "@material-ui/core/Chip";
import Checkbox from "@material-ui/core/Checkbox";
import ListItemText from "@material-ui/core/ListItemText";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import useWhatsApps from "../../hooks/useWhatsApps";
import { filterOutboundConnections } from "../../utils/channels";
import { allowedWhatsappIdsOf } from "../../utils/connectionScope";

import { Can } from "../Can";
import { Tab, Tabs } from "@material-ui/core";
import { getBackendUrl } from "../../config";
import TabPanel from "../TabPanel";
import AvatarUploader from "../AvatarUpload";
import {
  usePanelStyles,
  ModalTitle,
  FormSection,
  SettingRow,
  DangerButton,
} from "../FormFields/panel";

const backendUrl = getBackendUrl();

const formatDateForInput = (date) => {
  if (!date) return '';
  
  // Se já está no formato YYYY-MM-DD, retorna como está
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }
  
  // Se é uma data ISO ou objeto Date, converte para YYYY-MM-DD
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  
  // Usar métodos locais para evitar problemas de timezone
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

const parseDateFromInput = (dateString) => {
  if (!dateString) return null;
  
  // Se já está no formato YYYY-MM-DD, retorna como está
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }
  
  // Se é uma data ISO, extrai apenas a parte da data
  if (dateString.includes('T')) {
    return dateString.split('T')[0];
  }
  
  return dateString;
};

const useStyles = makeStyles(() => ({
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  // a lista de permissões já traz o filete de cada linha: sem respiro extra no topo
  permissionList: {
    "& > *:first-child": { paddingTop: 2 },
  },
  connectionChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
  },
}));

// menu das conexões abre logo abaixo do campo (igual ao de filas)
const connectionMenuProps = {
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
  getContentAnchorEl: null,
};

// Opção de permissão: rótulo e explicação à esquerda, seletor à direita.
// Os valores gravados vêm de quem chama (há campos `enabled|disabled`,
// `enable|disable`, booleano e "true"/"false" — não unificar).
const PermissionRow = ({ name, label, description, children }) => (
  <SettingRow label={label} description={description} wide>
    <FormControl variant="outlined" size="small" fullWidth>
      <Field as={Select} name={name} required inputProps={{ "aria-label": label }}>
        {children}
      </Field>
    </FormControl>
  </SettingRow>
);

const UserSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  password: Yup.string().min(5, "Too Short!").max(50, "Too Long!"),
  email: Yup.string().email("Invalid email").required("Required"),
  allHistoric: Yup.string().nullable(),
});


const UserModal = ({ open, onClose, userId }) => {
  const classes = useStyles();
  const p = usePanelStyles();

  const initialState = {
    name: "",
    email: "",
    password: "",
    birthDate: "",
    profile: "user",
    startWork: "00:00",
    endWork: "23:59",
    farewellMessage: "",
    allTicket: "enable",
    allowGroup: false,
    defaultTheme: "light",
    defaultMenu: "open",
    allHistoric: "enabled",
    allUserChat: "enabled",
    userClosePendingTicket: "enabled",
    showDashboard: "enabled",
    allowRealTime: "enabled",
    allowConnections: "enabled",
    showContacts: "enabled",
    showCampaign: "enabled",
    showFlow: "enabled",
    finalizacaoComValorVendaAtiva: "false",
    allowSeeMessagesInPendingTickets: "enabled"
  };

  const { user: loggedInUser } = useContext(AuthContext);

  const [user, setUser] = useState(initialState);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [whatsappId, setWhatsappId] = useState(false);
  // "Conexões que pode atender": [] = todas (sem restrição)
  const [allowedWhatsappIds, setAllowedWhatsappIds] = useState([]);
  // const [allTicket, setAllTicket] = useState("disable");
  const { loading, whatsApps } = useWhatsApps();
  const [profileUrl, setProfileUrl] = useState(null);
  const [tab, setTab] = useState("general");
  const [avatar, setAvatar] = useState(null);
  const startWorkRef = useRef();
  const endWorkRef = useRef();

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) return;
      try {
        const { data } = await api.get(`/users/${userId}`);
        setUser((prevState) => ({
          ...prevState,
          ...data,
          allTicket:
            data.allTicket === "enable" || data.allTicket === "enabled"
              ? "enable"
              : "disable",
          finalizacaoComValorVendaAtiva: data.finalizacaoComValorVendaAtiva
            ? "true"
            : "false",
          allowSeeMessagesInPendingTickets: data.allowSeeMessagesInPendingTickets === "enabled" ? "enabled" : "disabled",
          farewellMessage: data.farewellMessage || "",
          // Formatar a data corretamente
          birthDate: formatDateForInput(data.birthDate)
        }));

        const { profileImage } = data;
        setProfileUrl(
          `${backendUrl}/public/company${data.companyId}/user/${profileImage}`
        );

        const userQueueIds = data.queues?.map((queue) => queue.id);
        setSelectedQueueIds(userQueueIds);
        setWhatsappId(data.whatsappId ? data.whatsappId : "");
        setAllowedWhatsappIds(allowedWhatsappIdsOf(data) || []);
      } catch (err) {
        toastError(err);
      }
    };

    fetchUser();
  }, [userId, open]);

  // usuário novo começa sem restrição (não herda a lista do último usuário aberto)
  useEffect(() => {
    if (!userId) setAllowedWhatsappIds([]);
  }, [userId, open]);

  const handleClose = () => {
    onClose();
    setUser(initialState);
    setAllowedWhatsappIds([]);
  };

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const getBasename = (filepath) => {
    if (!filepath) return '';
    // Remove query strings e hashes
    const cleanPath = filepath.split('?')[0].split('#')[0];
    // Pega o último segmento após /
    const segments = cleanPath.split('/');
    return segments[segments.length - 1];
  };

  // UserModal/index.js - Função handleSaveUser corrigida
  const handleSaveUser = async (values) => {
    const uploadAvatar = async (userId) => {
      if (!avatar || typeof avatar !== 'object') return null;

      const formData = new FormData();
      formData.append("userId", userId);
      formData.append("typeArch", "user");
      formData.append("profileImage", avatar);

      try {
        const { data } = await api.post(
          `/users/${userId}/media-upload`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        return data.user.profileImage;
      } catch (error) {
        console.error("Erro no upload da imagem:", error);
        throw error;
      }
    };

    const userData = {
      ...values,
      whatsappId,
      queueIds: selectedQueueIds,
      // [] = todas as conexões (sem restrição); o servidor descarta id que não for da empresa
      allowedWhatsappIds,
      finalizacaoComValorVendaAtiva: values.finalizacaoComValorVendaAtiva === "true",
      birthDate: parseDateFromInput(values.birthDate),
      allowSeeMessagesInPendingTickets: values.allowSeeMessagesInPendingTickets === "enabled" ? "enabled" : "disabled"
    };

    try {
      let responseData;

      if (userId) {
        // Atualizar usuário existente
        const { data } = await api.put(`/users/${userId}`, userData);
        responseData = data;

        // Upload da imagem se houver uma nova
        if (avatar && typeof avatar === 'object') {
          const newProfileImage = await uploadAvatar(userId);
          if (newProfileImage) {
            responseData.profileImage = newProfileImage;

            // Atualizar localStorage se for o usuário logado
            if (userId === loggedInUser.id) {
              localStorage.setItem("profileImage", newProfileImage);
            }
          }
        }
      } else {
        // Criar novo usuário
        const { data } = await api.post("/users", userData);
        responseData = data.user;

        // Upload da imagem se houver
        if (avatar && typeof avatar === 'object' && responseData.id) {
          const newProfileImage = await uploadAvatar(responseData.id);
          if (newProfileImage) {
            responseData.profileImage = newProfileImage;
          }
        }
      }

      handleClose();
      toast.success(i18n.t("userModal.success"));

      // Recarregar página se for o usuário logado para atualizar a interface
      if (userId === loggedInUser.id) {
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (err) {
      toastError(err);
    }
  };

  // "Habilitado" primeiro em todos os seletores; os valores gravados continuam os de cada campo
  const enabledDisabledOptions = [
    <MenuItem key="enabled" value="enabled">
      {i18n.t("userModal.form.allHistoricEnabled")}
    </MenuItem>,
    <MenuItem key="disabled" value="disabled">
      {i18n.t("userModal.form.allHistoricDisabled")}
    </MenuItem>,
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      classes={{ paper: p.dialogPaper }}
    >
      <ModalTitle
        id="form-dialog-title"
        title={
          userId
            ? `${i18n.t("userModal.title.edit")}`
            : `${i18n.t("userModal.title.add")}`
        }
        subtitle="Dados de acesso, filas, expediente e o que esta pessoa pode ver e fazer."
        onClose={handleClose}
      />
      <Formik
        initialValues={user}
        enableReinitialize={true}
        validationSchema={UserSchema}
        onSubmit={(values, actions) => {
          setTimeout(() => {
            handleSaveUser(values);
            actions.setSubmitting(false);
          }, 400);
        }}
      >
        {({ touched, errors, isSubmitting, setFieldValue }) => (
          <Form className={p.modalForm}>
            <Tabs
              value={tab}
              indicatorColor="primary"
              textColor="primary"
              scrollButtons="auto"
              variant="scrollable"
              onChange={handleTabChange}
              className={p.modalTabs}
            >
              <Tab
                label={i18n.t("userModal.tabs.general")}
                value={"general"}
              />
              <Tab
                label={i18n.t("userModal.tabs.permissions")}
                value={"permissions"}
              />
            </Tabs>
            <DialogContent className={clsx(p.modalBody, p.labelsAbove)}>
              <TabPanel value={tab} name={"general"}>
                <FormSection>
                  <AvatarUploader
                    setAvatar={setAvatar}
                    avatar={user.profileImage}
                    companyId={user.companyId}
                  >
                    {user.profileImage && (
                      <DangerButton
                        variant="text"
                        size="small"
                        onClick={() => {
                          user.profileImage = null;
                          setFieldValue("profileImage", null);
                          setAvatar(null);
                        }}
                      >
                        {i18n.t("userModal.title.removeImage")}
                      </DangerButton>
                    )}
                  </AvatarUploader>
                </FormSection>

                <FormSection
                  title="Identificação"
                  description="Nome exibido nos atendimentos e dados de entrada no sistema."
                >
                  <div className={p.grid2}>
                    <Field
                      as={TextField}
                      label={i18n.t("userModal.form.name")}
                      autoFocus
                      name="name"
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      variant="outlined"
                      size="small"
                      fullWidth
                    />
                    <Field
                      as={TextField}
                      label={i18n.t("userModal.form.email")}
                      name="email"
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                      variant="outlined"
                      size="small"
                      fullWidth
                    />
                    <Field
                      as={TextField}
                      label={i18n.t("userModal.form.password")}
                      type="password"
                      name="password"
                      error={touched.password && Boolean(errors.password)}
                      helperText={touched.password && errors.password}
                      variant="outlined"
                      size="small"
                      fullWidth
                    />
                    <Can
                      role={loggedInUser.profile}
                      perform="user-modal:editProfile"
                      yes={() => (
                        <FormControl variant="outlined" size="small" fullWidth>
                          <InputLabel id="profile-selection-input-label">
                            {i18n.t("userModal.form.profile")}
                          </InputLabel>

                          <Field
                            as={Select}
                            label={i18n.t("userModal.form.profile")}
                            name="profile"
                            labelId="profile-selection-input-label"
                            id="profile-selection"
                            required
                          >
                            <MenuItem value="admin">Administrador</MenuItem>
                            <MenuItem value="user">Atendente</MenuItem>
                          </Field>
                        </FormControl>
                      )}
                    />
                    <Field
                      as={TextField}
                      label="Data de Nascimento"
                      type="date"
                      name="birthDate"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      variant="outlined"
                      size="small"
                      helperText="Data de nascimento para notificações de aniversário"
                      onChange={(e) => {
                        const formattedDate = parseDateFromInput(e.target.value);
                        setFieldValue('birthDate', formattedDate);
                      }}
                    />
                  </div>
                </FormSection>

                <FormSection
                  title="Atendimento"
                  description="Filas em que atende, conexão usada por padrão, expediente e despedida."
                >
                  <div className={p.grid2}>
                    <Can
                      role={loggedInUser.profile}
                      perform="user-modal:editQueues"
                      yes={() => (
                        <div className={p.span2}>
                          <QueueSelect
                            selectedQueueIds={selectedQueueIds}
                            onChange={(values) => setSelectedQueueIds(values)}
                            margin="none"
                          />
                        </div>
                      )}
                    />
                    <Can
                      role={loggedInUser.profile}
                      perform="user-modal:editProfile"
                      yes={() => (
                        <FormControl
                          variant="outlined"
                          size="small"
                          className={p.span2}
                          fullWidth
                        >
                          <InputLabel>
                            {i18n.t("userModal.form.whatsapp")}
                          </InputLabel>
                          <Field
                            as={Select}
                            value={whatsappId}
                            onChange={(e) => setWhatsappId(e.target.value)}
                            label={i18n.t("userModal.form.whatsapp")}
                          >
                            <MenuItem value={""}>&nbsp;</MenuItem>
                            {filterOutboundConnections(whatsApps).map((whatsapp) => (
                              <MenuItem
                                key={whatsapp.id}
                                value={whatsapp.id}
                              >
                                {whatsapp.name}
                              </MenuItem>
                            ))}
                          </Field>
                        </FormControl>
                      )}
                    />
                    <Can
                      role={loggedInUser.profile}
                      perform="user-modal:editQueues"
                      yes={() => (
                        <FormControl
                          variant="outlined"
                          size="small"
                          className={p.span2}
                          fullWidth
                        >
                          <InputLabel id="allowed-whatsapps-label">
                            {i18n.t("userModal.form.allowedWhatsapps")}
                          </InputLabel>
                          <Select
                            multiple
                            displayEmpty
                            labelId="allowed-whatsapps-label"
                            label={i18n.t("userModal.form.allowedWhatsapps")}
                            value={allowedWhatsappIds}
                            onChange={(e) => setAllowedWhatsappIds(e.target.value)}
                            MenuProps={connectionMenuProps}
                            renderValue={(selected) =>
                              !selected || selected.length === 0 ? (
                                i18n.t("userModal.form.allowedWhatsappsAll")
                              ) : (
                                <div className={classes.connectionChips}>
                                  {selected.map((id) => (
                                    <Chip
                                      key={id}
                                      size="small"
                                      label={whatsApps.find((w) => w.id === id)?.name || `#${id}`}
                                    />
                                  ))}
                                </div>
                              )
                            }
                          >
                            {whatsApps.map((whatsapp) => (
                              <MenuItem key={whatsapp.id} value={whatsapp.id}>
                                <Checkbox
                                  size="small"
                                  color="primary"
                                  checked={allowedWhatsappIds.indexOf(whatsapp.id) > -1}
                                />
                                <ListItemText primary={whatsapp.name} />
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            {i18n.t("userModal.form.allowedWhatsappsHelp")}
                          </FormHelperText>
                        </FormControl>
                      )}
                    />
                    <Can
                      role={loggedInUser.profile}
                      perform="user-modal:editProfile"
                      yes={() => (
                        <>
                          <Field
                            as={TextField}
                            label={i18n.t("userModal.form.startWork")}
                            type="time"
                            ampm={"false"}
                            inputRef={startWorkRef}
                            InputLabelProps={{
                              shrink: true,
                            }}
                            inputProps={{
                              step: 600, // 5 min
                            }}
                            fullWidth
                            name="startWork"
                            error={
                              touched.startWork && Boolean(errors.startWork)
                            }
                            helperText={touched.startWork && errors.startWork}
                            variant="outlined"
                            size="small"
                          />
                          <Field
                            as={TextField}
                            label={i18n.t("userModal.form.endWork")}
                            type="time"
                            ampm={"false"}
                            inputRef={endWorkRef}
                            InputLabelProps={{
                              shrink: true,
                            }}
                            inputProps={{
                              step: 600, // 5 min
                            }}
                            fullWidth
                            name="endWork"
                            error={touched.endWork && Boolean(errors.endWork)}
                            helperText={touched.endWork && errors.endWork}
                            variant="outlined"
                            size="small"
                          />
                        </>
                      )}
                    />
                    <Field
                      as={TextField}
                      label={i18n.t("userModal.form.farewellMessage")}
                      type="farewellMessage"
                      multiline
                      rows={4}
                      fullWidth
                      name="farewellMessage"
                      error={
                        touched.farewellMessage &&
                        Boolean(errors.farewellMessage)
                      }
                      helperText={
                        touched.farewellMessage && errors.farewellMessage
                      }
                      variant="outlined"
                      size="small"
                      className={p.span2}
                    />
                  </div>
                </FormSection>

                <FormSection
                  title="Preferências de tela"
                  description="Como o sistema abre para este usuário."
                >
                  <div className={p.grid2}>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <InputLabel>
                        {i18n.t("userModal.form.defaultTheme")}
                      </InputLabel>

                      <Field
                        as={Select}
                        label={i18n.t("userModal.form.defaultTheme")}
                        name="defaultTheme"
                        type="defaultTheme"
                        required
                      >
                        <MenuItem value="light">
                          {i18n.t("userModal.form.defaultThemeLight")}
                        </MenuItem>
                        <MenuItem value="dark">
                          {i18n.t("userModal.form.defaultThemeDark")}
                        </MenuItem>
                      </Field>
                    </FormControl>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <InputLabel>
                        {i18n.t("userModal.form.defaultMenu")}
                      </InputLabel>

                      <Field
                        as={Select}
                        label={i18n.t("userModal.form.defaultMenu")}
                        name="defaultMenu"
                        type="defaultMenu"
                        required
                      >
                        <MenuItem value={"open"}>
                          {i18n.t("userModal.form.defaultMenuOpen")}
                        </MenuItem>
                        <MenuItem value={"closed"}>
                          {i18n.t("userModal.form.defaultMenuClosed")}
                        </MenuItem>
                      </Field>
                    </FormControl>
                  </div>
                </FormSection>
              </TabPanel>
              <TabPanel value={tab} name={"permissions"}>
                <Can
                  role={loggedInUser.profile}
                  perform="user-modal:editProfile"
                  no={() => (
                    <div className={p.notice}>
                      Somente administradores podem alterar as permissões.
                    </div>
                  )}
                  yes={() => (
                    <>
                      <FormSection
                        title="Atendimentos"
                        description="O que este usuário enxerga e pode fazer na caixa de atendimentos."
                      >
                        <div className={classes.permissionList}>
                          <PermissionRow
                            name="allTicket"
                            label={i18n.t("userModal.form.allTicket")}
                            description="Atendimentos que ainda não foram direcionados a uma fila."
                          >
                            <MenuItem value="enable">
                              {i18n.t("userModal.form.allTicketEnable")}
                            </MenuItem>
                            <MenuItem value="disable">
                              {i18n.t("userModal.form.allTicketDisable")}
                            </MenuItem>
                          </PermissionRow>
                          <PermissionRow
                            name="allowGroup"
                            label={i18n.t("userModal.form.allowGroup")}
                            description="Conversas de grupos do WhatsApp."
                          >
                            <MenuItem value={true}>
                              {i18n.t("userModal.form.allTicketEnable")}
                            </MenuItem>
                            <MenuItem value={false}>
                              {i18n.t("userModal.form.allTicketDisable")}
                            </MenuItem>
                          </PermissionRow>
                          <PermissionRow
                            name="allHistoric"
                            label={i18n.t("userModal.form.allHistoric")}
                            description="Histórico das filas em que o usuário não está."
                          >
                            {enabledDisabledOptions}
                          </PermissionRow>
                          <PermissionRow
                            name="allUserChat"
                            label={i18n.t("userModal.form.allUserChat")}
                            description="Libera o “mostrar todos”, com os atendimentos dos colegas."
                          >
                            {enabledDisabledOptions}
                          </PermissionRow>
                          <PermissionRow
                            name="userClosePendingTicket"
                            label={i18n.t("userModal.form.userClosePendingTicket")}
                            description="Encerrar um atendimento que ainda está aguardando."
                          >
                            {enabledDisabledOptions}
                          </PermissionRow>
                          <PermissionRow
                            name="allowSeeMessagesInPendingTickets"
                            label={i18n.t("userModal.form.allowSeeMessagesInPendingTickets")}
                            description="Ler as mensagens antes de aceitar o atendimento."
                          >
                            {enabledDisabledOptions}
                          </PermissionRow>
                          <PermissionRow
                            name="finalizacaoComValorVendaAtiva"
                            label="Finalização com Valor de Venda"
                            description="Ao resolver, pede o valor da venda do atendimento."
                          >
                            <MenuItem value="true">Habilitado</MenuItem>
                            <MenuItem value="false">Desabilitado</MenuItem>
                          </PermissionRow>
                        </div>
                      </FormSection>

                      <FormSection
                        title="Telas e menus"
                        description="Áreas do sistema que aparecem para este usuário."
                      >
                        <div className={classes.permissionList}>
                          <PermissionRow
                            name="showDashboard"
                            label={i18n.t("userModal.form.showDashboard")}
                            description="Indicadores gerais de atendimento."
                          >
                            {enabledDisabledOptions}
                          </PermissionRow>
                          <PermissionRow
                            name="allowRealTime"
                            label={i18n.t("userModal.form.allowRealTime")}
                            description="Acompanhamento dos atendimentos em tempo real."
                          >
                            {enabledDisabledOptions}
                          </PermissionRow>
                          <PermissionRow
                            name="allowConnections"
                            label={i18n.t("userModal.form.allowConnections")}
                            description="Tela de conexões e as ações sobre cada canal."
                          >
                            {enabledDisabledOptions}
                          </PermissionRow>
                          <PermissionRow
                            name="showContacts"
                            label={i18n.t("userModal.form.showContacts")}
                            description="Lista de contatos da empresa."
                          >
                            {enabledDisabledOptions}
                          </PermissionRow>
                          <PermissionRow
                            name="showCampaign"
                            label={i18n.t("userModal.form.showCampaign")}
                            description="Menu de campanhas e listas de envio."
                          >
                            {enabledDisabledOptions}
                          </PermissionRow>
                          <PermissionRow
                            name="showFlow"
                            label={i18n.t("userModal.form.showFlow")}
                            description="Construtor de fluxos de conversa."
                          >
                            {enabledDisabledOptions}
                          </PermissionRow>
                        </div>
                      </FormSection>
                    </>
                  )}
                />
              </TabPanel>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} disabled={isSubmitting}>
                {i18n.t("userModal.buttons.cancel")}
              </Button>
              <Button
                type="submit"
                color="primary"
                disabled={isSubmitting}
                variant="contained"
                className={classes.btnWrapper}
              >
                {userId
                  ? `${i18n.t("userModal.buttons.okEdit")}`
                  : `${i18n.t("userModal.buttons.okAdd")}`}
                {isSubmitting && (
                  <CircularProgress
                    size={24}
                    className={classes.buttonProgress}
                  />
                )}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default UserModal;
